import * as _ from 'lodash';
import { generateUUID } from 'app/shared/uuid.service';
import { IMapLayerProvider } from 'app/config/IMapLayerProvider';

interface JSTerrainProvider {
    name: string;
    url?: string;
    type: string;
    relativeUrl?: boolean;
    terrainDetailLevel: number;
    terrainLayerCreationDate?: string;
    cesiumIonId?: number;
    uuid: string;
}

const DEFAULT_TERRAIN_PROVIDER_URL = '';
const DEFAULT_ARCGIS_TERRAIN_PROVIDER_URL =
    'https://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer';

export enum ImsTerrainProviderType {
    ION = 'ion',
    CUSTOM = 'custom',
    ARCGIS = 'arcgis',
}

export const AVAILABLE_IMS_TERRAIN_PROVIDER_TYPES = [
    {
        name: ImsTerrainProviderType.ION,
        displayName: 'Cesium Ion Terrain',
    },
    {
        name: ImsTerrainProviderType.CUSTOM,
        displayName: 'Custom Terrain',
    },
    {
        name: ImsTerrainProviderType.ARCGIS,
        displayName: 'ArcGIS Terrain',
    },
];

const AVAILABLE_TYPE_LIST = AVAILABLE_IMS_TERRAIN_PROVIDER_TYPES.map((t) => t.name.toString());

const TYPE_SPECIFIC_FIELDS = {
    [ImsTerrainProviderType.ION]: ['cesiumIonId'],
    [ImsTerrainProviderType.CUSTOM]: ['url', 'relativeUrl'],
    [ImsTerrainProviderType.ARCGIS]: ['url'],
};

/**
 * Holds the logic for dealing with a terrain provider. It is an element
 * of a TerrainProviderList
 */
export class ImsTerrainProvider implements IMapLayerProvider {
    private _pojoProvider: JSTerrainProvider;
    private _name: string;
    private _url: string;
    private _cesiumIonId: number;
    private _terrainDetailLevel: number = 13; // set to a default
    private _uuid: string;
    private _relativeUrl: boolean;
    private _type: string;
    private _terrainLayerCreationDate: string = null;

    /**
     * Accepts an POJO of the ITerrainProvider interface.
     * @param {JSTerrainProvider} obj
     */
    constructor(obj?: JSTerrainProvider) {
        if (!obj) {
            obj = {} as any;
        }

        if (!obj.type && obj.cesiumIonId) {
            obj.type = ImsTerrainProviderType.ION;
        }

        if (!obj.type && obj.url) {
            obj.type = ImsTerrainProviderType.CUSTOM;
        }

        // Assign defaults
        obj = Object.assign(
            {},
            {
                name: 'New Terrain Layer',
                url: DEFAULT_TERRAIN_PROVIDER_URL,
                type: ImsTerrainProviderType.CUSTOM,
                uuid: generateUUID(),
                relativeUrl: false,
            },
            obj,
        );

        this._type = obj.type;

        this._cesiumIonId = obj.cesiumIonId;
        this._name = obj.name;
        this._url = obj.url;
        this._uuid = obj.uuid;

        this._relativeUrl = obj.relativeUrl;
        this._terrainDetailLevel = obj.terrainDetailLevel;

        this._pojoProvider = obj;
        this._terrainLayerCreationDate = obj.terrainLayerCreationDate;
    }

    get name(): string {
        return this._name;
    }

    set name(name: string) {
        this._name = name;
    }

    /**
     * This can be a relative URL
     * One of url or cesiumIonId is required
     * @returns {string}
     */
    get url(): string {
        return this._url;
    }

    set url(val: string) {
        this._url = val;
    }

    // One of url or cesiumIonId is required
    get cesiumIonId(): number {
        return this._cesiumIonId;
    }

    set cesiumIonId(id: number) {
        this._cesiumIonId = id;
    }

    // defaults to DEFAULT_TERRAIN_DETAIL_LEVEL
    get terrainDetailLevel(): number {
        return this._terrainDetailLevel;
    }

    set terrainDetailLevel(val: number) {
        this._terrainDetailLevel = val;
    }

    get terrainLayerCreationDate(): string {
        return this._terrainLayerCreationDate;
    }

    set terrainLayerCreationDate(val: string) {
        this._terrainLayerCreationDate = val;
    }

    get uuid(): string {
        return this._uuid;
    }

    get type(): string {
        return this._type;
    }

    set type(val: string) {
        if (!AVAILABLE_TYPE_LIST.includes(val)) {
            throw new Error(`Unhandled type: ${val}`);
        }

        if (val !== this._type) {
            // If the value has changed, clear out the unneeded values
            if (val === ImsTerrainProviderType.ION) {
                this._relativeUrl = undefined;
                this._url = undefined;
                this._cesiumIonId = 1;
            } else if (val === ImsTerrainProviderType.CUSTOM) {
                this._relativeUrl = false;
                this._url = DEFAULT_TERRAIN_PROVIDER_URL;
                this._cesiumIonId = undefined;
            } else if (val === ImsTerrainProviderType.ARCGIS) {
                this._relativeUrl = false;
                this._url = DEFAULT_ARCGIS_TERRAIN_PROVIDER_URL;
                this._cesiumIonId = undefined;
            } else {
                throw new Error(`Unhandled set to type: ${val}`);
            }
        }
        this._type = val;
    }

    /**
     * Is the URL a relative URL
     * @returns {boolean}
     */
    get relativeUrl(): boolean {
        return this._relativeUrl;
    }

    set relativeUrl(val: boolean) {
        this._relativeUrl = val;
    }

    /**
     * Return to a representation that is ready for jsonification for saving
     * to the API
     * @returns {JSTerrainProvider}
     */
    toJS(): JSTerrainProvider {
        let baseResult = {
            name: this._name,
            uuid: this._uuid,
            terrainDetailLevel: this._terrainDetailLevel,
            type: this._type,
        };

        if (this._type === ImsTerrainProviderType.ION) {
            baseResult = Object.assign(baseResult, {
                cesiumIonId: this._cesiumIonId,
            });
        } else if (this._type === ImsTerrainProviderType.CUSTOM) {
            baseResult = Object.assign(baseResult, {
                url: this._url,
                relativeUrl: this._relativeUrl,
                ...(this._relativeUrl
                    ? { terrainLayerCreationDate: this._terrainLayerCreationDate }
                    : {}),
            });
        } else if (this._type === ImsTerrainProviderType.ARCGIS) {
            baseResult = Object.assign(baseResult, {
                url: this._url,
            });
        } else {
            throw new Error(`ImsTerrainProvider.toJS() unhandled type: ${this._type}`);
        }
        return baseResult;
    }

    clone(): this {
        return new ImsTerrainProvider(this.toJS()) as any;
    }

    /**
     * Coarse differ
     * @param {ImsTerrainProvider} other
     * @returns {boolean} true if the other provider is different
     */
    isDiff(other: this): boolean {
        const typeSpecificFields = _.get(TYPE_SPECIFIC_FIELDS, other.type, []);

        return (
            this.name !== other.name ||
            this.terrainDetailLevel !== other.terrainDetailLevel ||
            this.type !== other.type ||
            typeSpecificFields.some((f) => this[f] !== other[f])
        );
    }
}
